import { Injectable } from '@angular/core';
import {
  BehaviorSubject,
  Observable,
} from 'rxjs';

import {
  DataLoaderService,
} from './data-loader.service';

export type GlobalCollectionName =
  | 'jobs'
  | 'quotations'
  | 'ledger';

export type HeaderSearchCategory =
  | 'all'
  | 'job'
  | 'quotation';

export type IndexedSearchCollection =
  | 'jobs'
  | 'quotations';

/**
 * Search ke waqt raw nested document dobara traverse nahi hoga.
 * Yeh index complete record se sirf ek martaba prepare hota hai.
 */
export interface GlobalSearchIndexRecord {
  id: string;
  data: any;
  normalizedValues: string[];
  searchText: string;
}

type CollectionState = {
  subject: BehaviorSubject<any[]>;
  loaded: boolean;
  loadingPromise: Promise<any[]> | null;
  requestVersion: number;

  searchIndexSubject:
    BehaviorSubject<GlobalSearchIndexRecord[]> | null;
  searchIndexLoaded: boolean;
  searchIndexPromise:
    Promise<GlobalSearchIndexRecord[]> | null;
  searchIndexVersion: number;
};

@Injectable({
  providedIn: 'root',
})
export class GlobalDataCacheService {
  private readonly states =
    new Map<
      GlobalCollectionName,
      CollectionState
    >();

  constructor(
    private readonly dataLoader:
      DataLoaderService,
  ) {
    this.registerCollection('jobs', true);
    this.registerCollection('quotations', true);
    this.registerCollection('ledger', false);
  }

  /* =========================================================
     GENERIC COLLECTION ACCESS
     ========================================================= */

  collection$(
    collectionName: GlobalCollectionName,
  ): Observable<any[]> {
    return this
      .getState(collectionName)
      .subject
      .asObservable();
  }

  getCollection(
    collectionName: GlobalCollectionName,
  ): any[] {
    return this
      .getState(collectionName)
      .subject
      .value;
  }

  hasLoaded(
    collectionName: GlobalCollectionName,
  ): boolean {
    return this
      .getState(collectionName)
      .loaded;
  }

  isLoading(
    collectionName: GlobalCollectionName,
  ): boolean {
    return !!this
      .getState(collectionName)
      .loadingPromise;
  }

  async loadCollectionOnce(
    collectionName: GlobalCollectionName,
    forceRefresh = false,
  ): Promise<any[]> {
    if (forceRefresh) {
      return this.refreshCollection(
        collectionName,
      );
    }

    const state =
      this.getState(collectionName);

    if (state.loaded) {
      return state.subject.value;
    }

    if (state.loadingPromise) {
      return state.loadingPromise;
    }

    const requestPromise =
      this.fetchCollection(
        collectionName,
      ).finally(() => {
        if (
          state.loadingPromise ===
          requestPromise
        ) {
          state.loadingPromise = null;
        }
      });

    state.loadingPromise =
      requestPromise;

    return requestPromise;
  }

  async refreshCollection(
    collectionName: GlobalCollectionName,
  ): Promise<any[]> {
    const state =
      this.getState(collectionName);

    /*
     * Existing request ko duplicate nahi karte.
     * Us request ke complete hone ke baad next explicit refresh
     * dobara Firestore hit kar sakti hai.
     */
    if (state.loadingPromise) {
      return state.loadingPromise;
    }

    this.dataLoader.invalidate(
      collectionName,
    );

    state.loaded = false;
    state.searchIndexLoaded = false;

    const requestPromise =
      this.fetchCollection(
        collectionName,
      ).finally(() => {
        if (
          state.loadingPromise ===
          requestPromise
        ) {
          state.loadingPromise = null;
        }
      });

    state.loadingPromise =
      requestPromise;

    return requestPromise;
  }

  clearCollectionMemory(
    collectionName: GlobalCollectionName,
  ): void {
    const state =
      this.getState(collectionName);

    /*
     * Running request cancel nahi hoti, isliye version invalidate
     * karte hain taake stale response state ko overwrite na kare.
     */
    state.requestVersion++;
    state.searchIndexVersion++;

    state.loaded = false;
    state.loadingPromise = null;
    state.subject.next([]);

    state.searchIndexLoaded = false;
    state.searchIndexPromise = null;
    state.searchIndexSubject?.next([]);
  }

  clearAllMemory(): void {
    this.states.forEach(
      (_state, collectionName) => {
        this.clearCollectionMemory(
          collectionName,
        );
      },
    );
  }

  /* =========================================================
     JOBS
     ========================================================= */

  get jobs$(): Observable<any[]> {
    return this.collection$('jobs');
  }

  get jobs(): any[] {
    return this.getCollection('jobs');
  }

  get hasLoadedJobs(): boolean {
    return this.hasLoaded('jobs');
  }

  loadJobsOnce(
    forceRefresh = false,
  ): Promise<any[]> {
    return this.loadCollectionOnce(
      'jobs',
      forceRefresh,
    );
  }

  refreshJobs(): Promise<any[]> {
    return this.refreshCollection(
      'jobs',
    );
  }

  upsertJob(
    jobId: string,
    patch: any,
  ): void {
    this.upsertCollectionRecord(
      'jobs',
      jobId,
      patch,
    );
  }

  removeJob(
    jobId: string,
  ): void {
    this.removeCollectionRecord(
      'jobs',
      jobId,
    );
  }

  /* =========================================================
     QUOTATIONS
     ========================================================= */

  get quotations$(): Observable<any[]> {
    return this.collection$(
      'quotations',
    );
  }

  get quotations(): any[] {
    return this.getCollection(
      'quotations',
    );
  }

  get hasLoadedQuotations(): boolean {
    return this.hasLoaded(
      'quotations',
    );
  }

  loadQuotationsOnce(
    forceRefresh = false,
  ): Promise<any[]> {
    return this.loadCollectionOnce(
      'quotations',
      forceRefresh,
    );
  }

  refreshQuotations(): Promise<any[]> {
    return this.refreshCollection(
      'quotations',
    );
  }

  upsertQuotation(
    quotationId: string,
    patch: any,
  ): void {
    this.upsertCollectionRecord(
      'quotations',
      quotationId,
      patch,
    );
  }

  removeQuotation(
    quotationId: string,
  ): void {
    this.removeCollectionRecord(
      'quotations',
      quotationId,
    );
  }

  /* =========================================================
     LEDGER
     ========================================================= */

  get ledger$(): Observable<any[]> {
    return this.collection$('ledger');
  }

  get ledger(): any[] {
    return this.getCollection('ledger');
  }

  get hasLoadedLedger(): boolean {
    return this.hasLoaded('ledger');
  }

  loadLedgerOnce(
    forceRefresh = false,
  ): Promise<any[]> {
    return this.loadCollectionOnce(
      'ledger',
      forceRefresh,
    );
  }

  refreshLedger(): Promise<any[]> {
    return this.refreshCollection(
      'ledger',
    );
  }

  upsertLedgerRecord(
    ledgerId: string,
    patch: any,
  ): void {
    this.upsertCollectionRecord(
      'ledger',
      ledgerId,
      patch,
    );
  }

  removeLedgerRecord(
    ledgerId: string,
  ): void {
    this.removeCollectionRecord(
      'ledger',
      ledgerId,
    );
  }

  /* =========================================================
     PREPARED SEARCH INDEX
     ========================================================= */

  get jobSearchIndex$():
    Observable<GlobalSearchIndexRecord[]> {
    return this.getSearchIndexSubject(
      'jobs',
    ).asObservable();
  }

  get jobSearchIndex():
    GlobalSearchIndexRecord[] {
    return this.getSearchIndexSubject(
      'jobs',
    ).value;
  }

  get quotationSearchIndex$():
    Observable<GlobalSearchIndexRecord[]> {
    return this.getSearchIndexSubject(
      'quotations',
    ).asObservable();
  }

  get quotationSearchIndex():
    GlobalSearchIndexRecord[] {
    return this.getSearchIndexSubject(
      'quotations',
    ).value;
  }

  async ensureSearchIndex(
    collectionName: IndexedSearchCollection,
  ): Promise<GlobalSearchIndexRecord[]> {
    const state =
      this.getState(collectionName);

    if (!state.loaded) {
      await this.loadCollectionOnce(
        collectionName,
      );
    }

    if (state.searchIndexLoaded) {
      return this
        .getSearchIndexSubject(
          collectionName,
        )
        .value;
    }

    if (state.searchIndexPromise) {
      return state.searchIndexPromise;
    }

    return this.scheduleSearchIndexBuild(
      collectionName,
      state.subject.value,
    );
  }

  /* =========================================================
     HEADER SEARCH LAZY LOADING
     ========================================================= */

  async loadHeaderCategoryOnce(
    category: HeaderSearchCategory,
  ): Promise<void> {
    switch (category) {
      case 'job':
        /*
         * Job results ledger ka wait nahi karenge.
         * Ledger enrichment header separately background mein load karega.
         */
        await this.loadAndPrepareSearch(
          'jobs',
        );
        return;

      case 'quotation':
        await this.loadAndPrepareSearch(
          'quotations',
        );
        return;

      case 'all':
      default:
        await Promise.all([
          this.loadAndPrepareSearch(
            'jobs',
          ),
          this.loadAndPrepareSearch(
            'quotations',
          ),
        ]);
        return;
    }
  }

  async loadGlobalSearchDataOnce(): Promise<{
    jobs: any[];
    quotations: any[];
    ledger: any[];
  }> {
    const [
      jobs,
      quotations,
      ledger,
    ] = await Promise.all([
      this.loadJobsOnce(),
      this.loadQuotationsOnce(),
      this.loadLedgerOnce(),
    ]);

    await Promise.all([
      this.ensureSearchIndex('jobs'),
      this.ensureSearchIndex('quotations'),
    ]);

    return {
      jobs,
      quotations,
      ledger,
    };
  }

  async loadJobIntelligenceDataOnce(): Promise<{
    jobs: any[];
    ledger: any[];
  }> {
    const [
      jobs,
      ledger,
    ] = await Promise.all([
      this.loadJobsOnce(),
      this.loadLedgerOnce(),
    ]);

    await this.ensureSearchIndex('jobs');

    return {
      jobs,
      ledger,
    };
  }

  /* =========================================================
     LOCAL CACHE MUTATION
     ========================================================= */

  upsertCollectionRecord(
    collectionName: GlobalCollectionName,
    recordId: string,
    patch: any,
  ): void {
    const state =
      this.getState(collectionName);

    const normalizedId =
      String(recordId || '').trim();

    if (!normalizedId) {
      console.warn(
        'GlobalDataCacheService: cannot upsert record without an ID.',
      );
      return;
    }

    const records = [
      ...state.subject.value,
    ];

    const index =
      records.findIndex(
        record =>
          this.getRecordId(record) ===
          normalizedId,
      );

    const existingRecord =
      index >= 0
        ? records[index]
        : {};

    const updatedRecord = {
      ...existingRecord,
      ...(patch || {}),
      id: normalizedId,
    };

    if (index >= 0) {
      records[index] = updatedRecord;
    } else {
      records.unshift(updatedRecord);
    }

    state.subject.next(records);
    state.loaded = true;

    if (this.isIndexedCollection(collectionName)) {
      this.upsertSearchIndexRecord(
        collectionName,
        updatedRecord,
      );
    }
  }

  removeCollectionRecord(
    collectionName: GlobalCollectionName,
    recordId: string,
  ): void {
    const state =
      this.getState(collectionName);

    const normalizedId =
      String(recordId || '').trim();

    if (!normalizedId) {
      return;
    }

    state.subject.next(
      state.subject.value.filter(
        record =>
          this.getRecordId(record) !==
          normalizedId,
      ),
    );

    state.loaded = true;

    if (this.isIndexedCollection(collectionName)) {
      const subject =
        this.getSearchIndexSubject(
          collectionName,
        );

      subject.next(
        subject.value.filter(
          record =>
            record.id !== normalizedId,
        ),
      );
    }
  }

  /* =========================================================
     INTERNAL METHODS
     ========================================================= */

  private registerCollection(
    collectionName: GlobalCollectionName,
    searchable: boolean,
  ): void {
    this.states.set(
      collectionName,
      {
        subject:
          new BehaviorSubject<any[]>([]),
        loaded: false,
        loadingPromise: null,
        requestVersion: 0,

        searchIndexSubject:
          searchable
            ? new BehaviorSubject<
                GlobalSearchIndexRecord[]
              >([])
            : null,
        searchIndexLoaded: false,
        searchIndexPromise: null,
        searchIndexVersion: 0,
      },
    );
  }

  private getState(
    collectionName: GlobalCollectionName,
  ): CollectionState {
    const state =
      this.states.get(collectionName);

    if (!state) {
      throw new Error(
        `GlobalDataCacheService: collection "${collectionName}" is not registered.`,
      );
    }

    return state;
  }

  private getSearchIndexSubject(
    collectionName: IndexedSearchCollection,
  ): BehaviorSubject<GlobalSearchIndexRecord[]> {
    const subject =
      this.getState(collectionName)
        .searchIndexSubject;

    if (!subject) {
      throw new Error(
        `GlobalDataCacheService: collection "${collectionName}" has no search index.`,
      );
    }

    return subject;
  }

  private isIndexedCollection(
    collectionName: GlobalCollectionName,
  ): collectionName is IndexedSearchCollection {
    return (
      collectionName === 'jobs' ||
      collectionName === 'quotations'
    );
  }

  private getRecordId(
    record: any,
  ): string {
    return String(
      record?.id ||
      record?.key ||
      record?.docId ||
      '',
    ).trim();
  }

  private async fetchCollection(
    collectionName: GlobalCollectionName,
  ): Promise<any[]> {
    const state =
      this.getState(collectionName);

    const requestVersion =
      ++state.requestVersion;

    try {
      const records =
        await this.dataLoader
          .listOncePromise<any>(
            collectionName,
            [],
            'all',
          );

      const normalizedRecords =
        (records || []).map(
          (record: any) => ({
            ...record,
            id:
              record?.id ||
              record?.key ||
              record?.docId ||
              '',
          }),
        );

      if (
        requestVersion !==
        state.requestVersion
      ) {
        return state.subject.value;
      }

      state.subject.next(
        normalizedRecords,
      );
      state.loaded = true;

      /*
       * Raw collection consumers ko data foran mil jata hai.
       * Search index background/yielding process mein prepare hota hai.
       */
      if (this.isIndexedCollection(collectionName)) {
        void this.scheduleSearchIndexBuild(
          collectionName,
          normalizedRecords,
        );
      }

      return normalizedRecords;
    } catch (error) {
      if (
        requestVersion ===
        state.requestVersion
      ) {
        state.loaded = false;
      }

      console.error(
        `GlobalDataCacheService: failed to load "${collectionName}".`,
        error,
      );

      throw error;
    }
  }

  private async loadAndPrepareSearch(
    collectionName: IndexedSearchCollection,
  ): Promise<void> {
    await this.loadCollectionOnce(
      collectionName,
    );

    await this.ensureSearchIndex(
      collectionName,
    );
  }

  private scheduleSearchIndexBuild(
    collectionName: IndexedSearchCollection,
    records: any[],
  ): Promise<GlobalSearchIndexRecord[]> {
    const state =
      this.getState(collectionName);

    if (
      state.searchIndexPromise &&
      state.searchIndexLoaded === false
    ) {
      return state.searchIndexPromise;
    }

    const version =
      ++state.searchIndexVersion;

    state.searchIndexLoaded = false;

    const promise =
      this.buildSearchIndexAsync(
        records || [],
      )
        .then(indexRecords => {
          if (
            version ===
            state.searchIndexVersion
          ) {
            this.getSearchIndexSubject(
              collectionName,
            ).next(indexRecords);

            state.searchIndexLoaded = true;
          }

          return indexRecords;
        })
        .finally(() => {
          if (
            state.searchIndexPromise ===
            promise
          ) {
            state.searchIndexPromise = null;
          }
        });

    state.searchIndexPromise = promise;
    return promise;
  }

  private async buildSearchIndexAsync(
    records: any[],
  ): Promise<GlobalSearchIndexRecord[]> {
    const output:
      GlobalSearchIndexRecord[] = [];

    const yieldEvery = 100;

    for (
      let index = 0;
      index < records.length;
      index++
    ) {
      const record = records[index];

      output.push(
        this.buildSearchIndexRecord(
          record,
        ),
      );

      if (
        index > 0 &&
        index % yieldEvery === 0
      ) {
        await new Promise<void>(
          resolve => setTimeout(resolve, 0),
        );
      }
    }

    return output;
  }

  private buildSearchIndexRecord(
    record: any,
  ): GlobalSearchIndexRecord {
    const id =
      this.getRecordId(record);

    const normalizedValueSet =
      new Set<string>();

    const visited =
      new WeakSet<object>();

    const walk = (
      value: unknown,
    ): void => {
      if (
        value === null ||
        value === undefined
      ) {
        return;
      }

      if (
        typeof value === 'string' ||
        typeof value === 'number' ||
        typeof value === 'boolean'
      ) {
        const rawText =
          String(value).trim();

        /*
         * Images/base64/very large generated text search ke liye useful nahi,
         * lekin memory aur indexing time bohat barha sakta hai.
         */
        if (
          !rawText ||
          rawText.startsWith('data:image/') ||
          rawText.length > 4000
        ) {
          return;
        }

        const normalized =
          this.normalizeSearchTerm(
            rawText,
          );

        if (normalized) {
          normalizedValueSet.add(
            normalized,
          );
        }

        return;
      }

      if (value instanceof Date) {
        normalizedValueSet.add(
          this.normalizeSearchTerm(
            value.toISOString(),
          ),
        );
        return;
      }

      if (Array.isArray(value)) {
        value.forEach(walk);
        return;
      }

      if (typeof value === 'object') {
        const objectValue =
          value as any;

        if (
          typeof objectValue?.toDate ===
          'function'
        ) {
          const date =
            objectValue.toDate();

          if (
            date instanceof Date &&
            !isNaN(date.getTime())
          ) {
            normalizedValueSet.add(
              this.normalizeSearchTerm(
                date.toISOString(),
              ),
            );
          }

          return;
        }

        if (visited.has(objectValue)) {
          return;
        }

        visited.add(objectValue);

        Object.values(
          objectValue,
        ).forEach(walk);
      }
    };

    walk(record);

    const normalizedValues =
      [...normalizedValueSet];

    return {
      id,
      data: record,
      normalizedValues,
      searchText:
        normalizedValues.join(' '),
    };
  }

  private upsertSearchIndexRecord(
    collectionName: IndexedSearchCollection,
    record: any,
  ): void {
    const state =
      this.getState(collectionName);

    const subject =
      this.getSearchIndexSubject(
        collectionName,
      );

    /*
     * Initial index abhi build ho raha ho to current raw records se
     * one-time rebuild schedule karna safest hai.
     */
    if (!state.searchIndexLoaded) {
      void this.scheduleSearchIndexBuild(
        collectionName,
        state.subject.value,
      );
      return;
    }

    const indexRecords = [
      ...subject.value,
    ];

    const id =
      this.getRecordId(record);

    const index =
      indexRecords.findIndex(
        item => item.id === id,
      );

    const updatedIndexRecord =
      this.buildSearchIndexRecord(
        record,
      );

    if (index >= 0) {
      indexRecords[index] =
        updatedIndexRecord;
    } else {
      indexRecords.unshift(
        updatedIndexRecord,
      );
    }

    subject.next(indexRecords);
  }

  private normalizeSearchTerm(
    value: unknown,
  ): string {
    return String(value ?? '')
      .toLowerCase()
      .trim()
      .replace(
        /[^a-z0-9@._/\-\s]/g,
        '',
      )
      .replace(/\s+/g, ' ');
  }
}
